import { IdentityLayout, IdentityHero } from "@speedycore/identity-ui";

const BULLETS = [
  "Effective: 3 October 2026",
  "Governs all SpeedyCore & Constructive OS apps",
  "Governing law: Queensland, Australia · Exclusive jurisdiction Brisbane",
  "18+ only — no parental / guardian consent pathway",
  "One acceptance covers the entire platform",
] as const;

export function TermsPage() {
  return (
    <IdentityLayout
      hero={
        <IdentityHero
          title="Speedy Systems Pty Ltd — Master Terms & Conditions"
          subtitle="These Terms govern your access to and use of all SpeedyCore platforms, apps, and services operated by Speedy Systems Pty Ltd (ACN 152 029 000)."
          bullets={BULLETS}
        />
      }
      form={
        <article
          style={{
            padding: "1.5rem 1.75rem",
            color: "#111827",
            lineHeight: 1.65,
            fontSize: "0.92rem",
          }}
        >
          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: 0 }}>1. Parties</h2>
          <p>
            These Terms are a binding agreement between <strong>Speedy Systems Pty Ltd</strong> ("Speedy Systems", "we", "us", or "our")
            and the person or legal entity accessing or using our services ("you" or "your").
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>2. Services Covered</h2>
          <p>These Terms apply equally to, and one acceptance covers, every product and service we operate, including without limitation:</p>
          <ul style={{ paddingLeft: "1.5rem" }}>
            <li>SpeedyCore Identity (auth.speedycore.com.au / dashboard.speedycore.com.au)</li>
            <li>Constructive OS (constructiveos.com.au and all white-label variants)</li>
            <li>PluginFactory AI, Creator Hub, Tradie Hub, and every white-label builder/creator/tradie site</li>
            <li>On-Site Payments, Stick It, Trade In, Dispatch Booking, Fill↔Fill, video hosting, land-data extraction</li>
            <li>Any future application or service that redirects authentication through SpeedyCore Identity</li>
          </ul>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>3. Accounts & Identity / Age of Majority</h2>
          <p>
            You must create and maintain one SpeedyCore identity. You are responsible for safeguarding credentials.
            We use RS256 signed tokens; token compromise through your own negligence is your liability.
            You warrant that all registration information is accurate and that you are <strong>at least 18 years
            of age</strong>, or the applicable age of majority in your jurisdiction (whichever is higher).
            SpeedyCore is for adult use only. No account may be opened by, or used on behalf of, a person under 18;
            <em> no parental, guardian, or carer-consent pathway is offered or honoured</em>. Any registration
            or submission by a person under 18 is unauthorised and will be terminated on discovery, with the
            associated personal information removed in accordance with our Privacy Policy (§11).
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>4. Acceptable Use</h2>
          <ul style={{ paddingLeft: "1.5rem" }}>
            <li>You will not use the services to violate any law or regulation.</li>
            <li>You will not upload malware, spam, infringing material, or unlawful content.</li>
            <li>You will not attempt to bypass authentication, rate limits, or platform isolation.</li>
            <li>You will not resell, sublicense, or white-label our tools without a separate written agreement.</li>
          </ul>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>4A. No Proselytising Clause</h2>
          <p>
            You must not use the platform to promote, advocate, or recruit others into political movements,
            religious beliefs, ideological positions, lifestyle doctrines, or any form of proselytising activity.
            This includes direct persuasion, indirect influence, targeted messaging, or content designed to convert,
            mobilise, or sway users toward political, religious, or ideological viewpoints. Reported violations
            may result in demonetisation, content removal, account restrictions, or termination at our sole
            discretion, acting reasonably.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>4B. No Hate Speech Clause</h2>
          <p>
            You must not post, upload, share, or transmit any content that expresses, promotes, incites, or
            encourages hatred, discrimination, harassment, or violence against individuals or groups based on
            race, ethnicity, nationality, gender, gender identity, sexual orientation, disability, medical
            condition, age, or any other protected attribute under Australian or Queensland anti-discrimination
            law. This includes slurs, dehumanising language, stereotypes, extremist ideology, or content intended
            to intimidate, threaten, or harm. Violations may result in content removal, demonetisation, account
            restrictions, or termination at our sole discretion, acting reasonably.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>5. Intellectual Property</h2>
          <p>
            All software, trademarks, designs, documentation, and proprietary processes are the property of Speedy Systems Pty Ltd
            or our licensors. You retain ownership of the content, plans, data, and files you upload or create using our services.
            You grant us a limited licence to host, process, transmit, and display your content solely to operate and improve the services.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>6. Subscriptions, Fees & Payments</h2>
          <p>
            Paid tiers are billed in advance on the schedule disclosed at checkout. Fees are non-refundable
            except as required by Australian Consumer Law. We may change pricing on 30 days' notice;
            continued use after the effective date constitutes acceptance.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>7. Disclaimers</h2>
          <p>
            The services are provided "AS IS" and "AS AVAILABLE" without warranties of any kind, whether express or implied,
            including but not limited to merchantability, fitness for a particular purpose, and non-infringement.
            Construction plans, measurements, compliance hints, and AI outputs are indicative only and must be independently
            verified by a licensed professional before being relied upon for any regulatory, financial, or construction decision.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>7A. AI Output — Accuracy &amp; User Responsibility</h2>
          <p>
            Many SpeedyCore services use artificial intelligence and machine-generated content ("AI output"),
            including — without limitation — generated text, code, calculations, estimates, measurements, drawings,
            plans, documents, SWMS and compliance material, quotes, recommendations, translations, summaries, and
            design or drafting assistance. By using the services you acknowledge and agree to all of the following:
          </p>
          <ul style={{ paddingLeft: "1.5rem" }}>
            <li>
              <strong>AI output can be wrong.</strong> AI output may be inaccurate, incomplete, outdated, misleading,
              unsuitable for your purpose, or unsafe. It may contain fabricated facts, figures, citations, or
              references ("hallucinations"), and it may reproduce errors present in your inputs or in training data.
            </li>
            <li>
              <strong>AI output is not professional advice.</strong> It is not, and must not be treated as, legal,
              financial, medical, engineering, construction, building-code, compliance, workplace health &amp; safety,
              insurance, accounting, or any other professional advice, unless it has been separately reviewed and
              expressly provided or endorsed by a qualified professional engaged by you.
            </li>
            <li>
              <strong>You must check everything.</strong> You are solely responsible for independently reviewing,
              checking, and verifying <em>all</em> AI output before relying on, publishing, submitting, installing,
              building from, deploying, or otherwise acting on it. This responsibility applies to calculations,
              measurements, drawings, plans, code, documents, SWMS, compliance material, quotes, recommendations,
              decisions, and actions generated or assisted by AI.
            </li>
            <li>
              <strong>You must verify inputs and context.</strong> You are responsible for confirming the accuracy of
              source data, measurements, and assumptions you provide, and for checking citations, permissions,
              licences, intellectual-property rights, privacy, security, and regulatory requirements that apply to
              both your inputs and the AI output you use.
            </li>
            <li>
              <strong>No guarantee of accuracy or fitness.</strong> We do not warrant that AI output is accurate,
              complete, current, error-free, or fit for any particular purpose, and we do not guarantee the
              uninterrupted availability of AI features or that AI features will operate without error.
            </li>
            <li>
              <strong>Get professional review where appropriate.</strong> Before relying on AI output in any
              regulated, safety-relevant, financial, or legally consequential context, you must obtain review by an
              appropriately qualified and licensed human professional.
            </li>
            <li>
              <strong>Never rely on AI output in emergencies or safety-critical situations.</strong> AI output must
              not be used as the sole basis for decisions affecting life, health, safety, structural integrity, or
              compliance with mandatory standards, without qualified human review and sign-off.
            </li>
            <li>
              <strong>Your responsibility does not transfer.</strong> Your obligation to review AI output survives
              your use of the services and applies equally where AI output is passed to, or relied on by, your
              clients, contractors, employees, or other downstream recipients.
            </li>
          </ul>
          <p>
            Acceptance of these Terms — including the separate AI accuracy acknowledgement recorded at sign-up and at
            each legal-version acceptance — is a condition of using any SpeedyCore service. If you are not willing to
            accept responsibility for checking AI output, you must not use AI-enabled features.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Speedy Systems Pty Ltd's total aggregate liability under these Terms
            (whether in contract, tort, statute, or otherwise) is limited to the total fees you actually paid us in the
            12 months preceding the event giving rise to the claim. In no event will we be liable for any indirect,
            incidental, special, consequential, or punitive damages.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>9. Termination</h2>
          <p>
            Either party may terminate the agreement for convenience with 30 days' written notice.
            We may suspend or terminate immediately for material breach, including fraud, illegal conduct,
            or security incidents attributable to you. Sections 5, 7, 7A, 8, 10, and 11 survive termination.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>10. Governing Law & Disputes</h2>
          <p>
            These Terms are governed by the laws of <strong>Queensland, Australia</strong>. You agree to submit to
            the <strong>exclusive jurisdiction of the courts of Queensland, including courts located in Brisbane</strong>,
            in relation to any dispute or matter arising out of or in connection with these Terms or your use of
            the services. Nothing in this section limits any right we may have under the Australian Consumer Law
            or any other non-excludable statutory right or remedy.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>11. Changes & Versioning / Mandatory Re-Acceptance</h2>
          <p>
            We may amend these Terms from time to time by bumping the legal version in SpeedyCore. Whenever the
            master Terms or master Privacy Policy are materially updated, <strong>you must read and affirmatively
            re-accept the latest versions before any further access to any SpeedyCore app is granted</strong>.
            Continued use <em>is not permitted</em> until your acceptance of the latest version is recorded with
            a timestamp, accepted versions, and signature hash (see §3 Accounts & Identity / Age of Majority
            acceptance confirmation language, and Privacy Policy §12). This page is the authoritative, current
            copy; prior versions are available on written request to legal@speedycore.com.au.
          </p>

          <h2 style={{ fontSize: "1.1rem", fontWeight: 700, marginTop: "1.25rem" }}>12. Contact</h2>
          <p>
            Legal notices and correspondence: <a href="mailto:legal@speedycore.com.au" style={{ color: "#1e3a8a", textDecoration: "underline" }}>legal@speedycore.com.au</a>
            <br />Speedy Systems Pty Ltd · Brisbane, Queensland, Australia
            <br /><em>Jurisdiction: Queensland, Australia · Exclusive courts of Queensland (including Brisbane)</em>
          </p>
        </article>
      }
    />
  );
}

export default TermsPage;
